/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ProductComponent } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCatalogId, ICoreContext } from '@msdyn365-commerce/core';
import { ProductAvailableQuantity, ProductPrice, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    DimensionSwatchDisplayTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    ObjectExtensions,
    PriceForProductSearchResultInput
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IListProductCollectionData } from './list-product-collection.data';
import { IListProductCollectionProps } from './list-product-collection.props.autogenerated';
import { getEstimatedAvailabilityAsync, getRelatedProductsAsync, getRelationTypesAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { attrNames } from '../../utilities/global-constants';
import { clsHelper } from '../../utilities/class-name-helper';

//==========================================================
// CLASS NAME UTILITY
//==========================================================
const BASE_CLASS = 'list-product-collection';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//==========================================================

export interface IProductCollectionViewProps extends IListProductCollectionProps<IListProductCollectionData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
    seeAllButton?: React.ReactNode;
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    currentProduct?: ProductSearchResult;
    products: ProductSearchResult[];
    filteredProducts: ProductSearchResult[]
    productPrices: ProductPrice[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    relatedProductSearch?: string;
    productType?: string;
    inputValue?: string;
    currentPage: number;
    desiredRelationType?: number;
    productAvailabilities?: ProductAvailableQuantity[];
}

/**
 * ProductCollection component.
 */
class ProductCollection extends React.PureComponent<IListProductCollectionProps<IListProductCollectionData>, IProductComponentState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    constructor(props: IListProductCollectionProps<IListProductCollectionData>) {
        super(props);
        this.productPrices = [];
        this.state = {
            products: [],
            filteredProducts: [],
            productPrices: this.productPrices || [],
            relatedProductSearch: '',
            inputValue: '',
            currentPage: 0,
            productAvailabilities: []
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {

        //check product type. if the product is model: get model related products, if part, get part related products
        const searchCriteria: ProductSearchCriteria = {
            Ids: [Number(this.props.context.request.urlTokens.recordId)],
            Context: {
                ChannelId: this.props.context.request.apiSettings.channelId,
                CatalogId: getCatalogId(this.props.context.request)
            },
            IncludeAttributes: true
        };
        const searchResults = await searchByCriteriaAsync({ callerContext: this.props.context.actionContext }, searchCriteria);
        this.setState({ currentProduct: searchResults.find(product => { return product.RecordId === Number(this.props.context.request.urlTokens.recordId); }) });
        //get all possible list names
        const relationTypes = await getRelationTypesAsync({ callerContext: this.props.context.actionContext }, Number(this.props.context.request.urlTokens.recordId), this.props.context.request.apiSettings.channelId, getCatalogId(this.props.context.request));

        const productType = this.state.currentProduct?.AttributeValues?.find(attribute => {
            return attribute.Name === attrNames.attributeValueWebProductType;
        })?.TextValue;

        this.setState({ productType });
        if (productType === 'Part' || productType === 'Kit') {
            this.setState({
                desiredRelationType: relationTypes.find(relation => {
                    return relation.Name === 'Alternate Part';
                })?.RecordId
            });
        }
        else if (productType === 'Model') {
            this.setState({
                desiredRelationType: relationTypes.find(relation => {
                    return relation.Name === 'Webstore Related';
                })?.RecordId
            });
        }

        //get all products for specified list name
        //@HERE
        const allProducts = await getRelatedProductsAsync({ callerContext: this.props.context.actionContext, queryResultSettings: { Paging: { Top: 6 } } }, Number(this.props.context.request.urlTokens.recordId), this.props.context.request.apiSettings.channelId, getCatalogId(this.props.context.request), this.state.desiredRelationType || 0);
        const productIds = allProducts.map(product => { return product.RecordId; });
        const unformattedAvailabilities = await getEstimatedAvailabilityAsync({ callerContext: this.props.context.actionContext }, { ProductIds: productIds, DefaultWarehouseOnly: true });
        const productAvailabilites = unformattedAvailabilities.ProductWarehouseInventoryAvailabilities?.map((product) => {
            return { ProductId: product.ProductId, AvailableQuantity: product.PhysicalAvailable };
        }) as ProductAvailableQuantity[];
        this.setState({ products: allProducts, productAvailabilities: productAvailabilites });

        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext.user.isAuthenticated) {
            await this._getActivePrice();
        }
    }

    public render(): JSX.Element | null {
        const { heading, className, /*shouldDisplaySeeAllButton*/ } = this.props.config;
        const { resources } = this.props;
        const products = this.state.relatedProductSearch !== '' ? this.state.filteredProducts : this.state.products;

        const productPrices = this.state.productPrices;
        //@CUSTOM: remove carousel and display grid instead
        //const isCarousel = this.props.config.layout === layout.carousel;
        const isCarousel = false;
        const headingLabel = heading ? `${heading.text}` : '';
        const headingComponent = heading && (
            <Msdyn365.Text
                className='list-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={headingLabel}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- product check for safety.
        const updatedProducts = products?.map((item: ProductSearchResult, index: number) => {
            const getProductData = this._getProduct(
                item,
                index,
                productPrices ? productPrices.find(p => p.ProductId === item.RecordId) : undefined
            );
            return !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
        });

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: `${headingLabel} ${resources.flipperPrevious}`,
                flipperNextLabel: `${headingLabel} ${resources.flipperNext}`,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('list-product-collection__content', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'list-product-collection__items'
            },
            isCarousel,

            products:
                ArrayExtensions.hasElements(updatedProducts) &&
                updatedProducts.filter(item => {
                    return item !== undefined;
                }),
            productPrices: this.productPrices,
        };
        return (
            <div className='list-product-collection'>
                {/*this.state.products.length && this.state.productType === 'Model' && this.rsmSearchBar({onSearch: this.rsmHandleSearch}) || null*/}
                {!!this.state.products.length && this.props.renderView(productCollectionViewProps) as React.ReactElement || null}
                {!!this.state.products.length && this.rsmRenderButtons()}
            </div>
        );
    }

    private rsmRenderButtons = (): JSX.Element => {
        return (
            <div className={cls('pagination')}>
                <button className={classnames(cls('pagination-link'), 'prev')} onClick={e => { this.handlePrevious(); }} disabled={this.state.currentPage === 0}>
                    <span className={cls('pagination-link-text')}>Previous</span>
                </button>
                <button className={classnames(cls('pagination-link'), 'next')} onClick={e => { this.handleNext(); }}>
                    <span className={cls('pagination-link-text')}>Next</span>
                </button>
            </div>
        );

    };

    private handleNext = () => {
        //@HERE
        getRelatedProductsAsync({ callerContext: this.props.context.actionContext, queryResultSettings: { Paging: { Top: 6, Skip: ((this.state.currentPage + 1) * 6) } } }, Number(this.props.context.request.urlTokens.recordId), this.props.context.request.apiSettings.channelId, getCatalogId(this.props.context.request), this.state.desiredRelationType || 0).then(result => {
            if (result.length) {
                this.setState({ products: result, currentPage: this.state.currentPage + 1 });
                const productIds = result.map(product => { return product.RecordId; });
                getEstimatedAvailabilityAsync({ callerContext: this.props.context.actionContext }, { ProductIds: productIds, DefaultWarehouseOnly: true }).then(availabilities => {
                    const productAvailabilites = availabilities.ProductWarehouseInventoryAvailabilities?.map((product) => {
                        return { ProductId: product.ProductId, AvailableQuantity: product.PhysicalAvailable };
                    }) as ProductAvailableQuantity[];
                    this.setState({ productAvailabilities: productAvailabilites });
                });
            }
        });
    };

    private handlePrevious = () => {
        //@HERE
        if (this.state.currentPage > 0) {
            getRelatedProductsAsync({ callerContext: this.props.context.actionContext, queryResultSettings: { Paging: { Top: 6, Skip: ((this.state.currentPage - 1) * 6) } } }, Number(this.props.context.request.urlTokens.recordId), this.props.context.request.apiSettings.channelId, getCatalogId(this.props.context.request), this.state.desiredRelationType || 0).then(result => {
                if (result.length) {
                    this.setState({ currentPage: this.state.currentPage - 1, products: result });
                    const productIds = result.map(product => { return product.RecordId; });
                    getEstimatedAvailabilityAsync({ callerContext: this.props.context.actionContext }, { ProductIds: productIds, DefaultWarehouseOnly: true }).then(availabilities => {
                        const productAvailabilites = availabilities.ProductWarehouseInventoryAvailabilities?.map((product) => {
                            return { ProductId: product.ProductId, AvailableQuantity: product.PhysicalAvailable };
                        }) as ProductAvailableQuantity[];
                        this.setState({ productAvailabilities: productAvailabilites });
                    });
                }
            });
        }
    };

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    /**
     * Get products dimension availabilities.
     * @param products - Products.
     * @returns Promise.
     */
    private readonly _getProductsDimensionsAvailabilities = async (products: ProductSearchResult[]): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        const validProducts = ArrayExtensions.validValues(products);
        if (
            ArrayExtensions.hasElements(validProducts) &&
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none)
        ) {
            const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
                new DimensionAvailabilitiesForProductSearchResultInput(
                    validProducts,
                    this.props.context as ICoreContext<IDimensionsApp>,
                    DimensionSwatchDisplayTypes.productCard
                ),
                this.props.context.actionContext
            );

            if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
                this.setState({ productsDimensionAvailabilities });
            }
        }
    };

    private readonly _getProduct = (
        product: ProductSearchResult,
        index: number,
        productPrice?: ProductPrice
    ): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const availability = this.state.productAvailabilities?.find(current => { return current.ProductId === product.RecordId; });
        const { imageSettings, allowBackNavigation } = this.props.config;
        const resources = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const productComparisonButtonSlot = ArrayExtensions.hasElements(this.props.slots.productComparisonButton)
            ? this.props.slots.productComparisonButton[0]
            : null;

        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        const priceResources: IPriceComponentResources = {
            priceRangeSeparator: resources.priceRangeSeparator
        };
        //@UPDATE: Changed primary image URL to display correct image
        const baseImgURL = this.props.context.request.apiSettings.baseImageUrl;
        if (!product.PrimaryImageUrl?.includes(baseImgURL)) {
            product.PrimaryImageUrl = `${baseImgURL}${product.PrimaryImageUrl}`;
        }

        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'list-product-collection__item',
                role: 'listitem',
                key: index
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    ratingCountAriaLabel={reviewCountAriaLabelValue}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product, productPrice }}
                    quickViewButton={quickviewSlot}
                    productComparisonButton={productComparisonButtonSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    priceResources={priceResources}
                    dimensionAvailabilities={this.state.productsDimensionAvailabilities?.find(
                        dimensionAvailability =>
                            ArrayExtensions.hasElements(dimensionAvailability) &&
                            dimensionAvailability[0].masterProductId === (product.MasterProductId ?? product.RecordId)
                    )}
                    config={{
                        Availability: availability?.AvailableQuantity,
                        Cart: this.props.data.cart.result && this.props.data.cart.result,
                        rsmListProductCollection: 'True'
                    }}
                />
            )
        };
    };

    /**
     * Get the active price for products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.state.products;
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            await this._getProductsDimensionsAvailabilities(products);
        }

        this.setState({ products });
    };

}

export default ProductCollection;
